/**
 * @file   src\Pages\Dashboard.tsx
 * @brief  Dashboard page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {
  Container,
  Row,
  Col,
  Button,
  useEffect,
  useOutletContext,
  useNavigate,
  useState,
  React,
} from "../utils/thirdpartyComponents";
import "../styles/dashboard.scss";
import FilterDropdown from "../components/FilterDropdown";
import Supporticon from "../assets/images/customer-support.svg";
import Usersicon from "../assets/images/users-icon.svg";
import Washlocationicon from "../assets/images/washlocation-icon.svg";
import Hardwareicon from "../assets/images/hardware-icon.svg";
import Washcompleteicon from "../assets/images/washcomplete-icon.svg";
import Revenueicon from "../assets/images/revenue-icon.svg";
import RightarrowIcon from "../assets/images/icons/rightarrow";
import SyncIcon from "../assets/images/icons/syncicon";
import { AppContextType } from "../interfaces/generalInterface";
import { pageURLs } from "../utils/pageUrls";
import { DashBoardApi } from "../utils/apiUrls";
import axios from "../config/axiosConfig";
import SmallLoader from "../components/SmallLoader";
import { userType } from "../utils/enums";
import { formatDateTimeToUserTime } from "../utils/helper";
import moment from "moment-timezone";

const Dashboard = () => {
  // Access the context
  const { updateData } = useOutletContext<AppContextType>();
  const [loading, setLoading] = useState(false);
  const [filterLabel, setFilterLabel] = useState("Current Month");
  const [dateFilterClass, setDateFilterClass] = useState("");
  const [locationFilterClass, setLocationFilterClass] = useState("");
  const [companyFilterClass, setCompanyFilterClass] = useState("");
  const [dashBoardFilterData, setDashBoardFilterData] = useState();
  const defaultCarwashDashboard = {
    carwash_company_count: 0,
    wash_location_count: 0,
    automatic_wash_location: 0,
    tunnel_wash_location: 0,
    selfBayCount: 0,
    dogBayCount: 0,
    vacuumBayCount: 0,
    app_users: 0,
    subscribed_users: 0,
    active_hardware: 0,
    inactive_hardware: 0,
    current_month_washes: 0,
    average_visit_permonth: 0,
    current_month_revenue: 0,
    last_device_sync_date: "",
  };

  const navigate = useNavigate();
  //const moment = _moment;

  // To display heading
  useEffect(() => {
    handleUpdate();
    const month = new Date().getMonth() + 1;
    const formValues = {
      company: null,
      location: null,
      month: month,
      year: new Date().getFullYear(),
      fromDate: null,
      toDate: null,
      dateFilterType: "month",
      defaultFilter: true,
      dateFilterChanged: false,
      companyFilterChanged: false,
      locationFilterChanged: false,
    };
    getDashboardData(formValues);
  }, []);

  // Update the data
  const handleUpdate = () => {
    updateData({
      heading: "Dashboard",
      url: "",
      showBackButton: false,
      showMainMenu: true,
      activeLink: "",
    });
  };

  const [dashboardCount, setDashboardCount] = useState(defaultCarwashDashboard);

  const handleClick = (type: string) => {
    navigate(`/superadmin/${pageURLs.hardware_details}`, {
      state: { type: type },
    });
  };

  const getDashboardData = async (payload: any) => {
    try {
      const timezoneOffset = new Date().getTimezoneOffset();
      let appendQueryString = false;
      let param = `?search=1&offset=${timezoneOffset}`;
      setDashBoardFilterData(payload);
      if (payload.company) {
        appendQueryString = true;
        param += "&carwashId=" + payload.company;
      }
      if (payload.location) {
        appendQueryString = true;
        param += "&locationId=" + payload.location;
      }
      if (payload.month && payload.dateFilterType === "month") {
        setFilterLabel("Current Month");
        appendQueryString = true;
        param += "&month=" + payload.month;
        param += "&year=" + new Date().getFullYear();
      }
      if (payload.year && payload.dateFilterType === "year") {
        setFilterLabel("Current Year");
        appendQueryString = true;
        param += "&year=" + payload.year;
      }
      if (payload.fromDate && payload.dateFilterType === "custom") {
        appendQueryString = true;
        const fromDateArray = payload.fromDate.split("/");
        const fromDate =
          fromDateArray[2] + "-" + fromDateArray[0] + "-" + fromDateArray[1];
        param += "&fromDate=" + moment(new Date(fromDate)).format("YYYY-MM-DD");
      }
      if (payload.toDate && payload.dateFilterType === "custom") {
        appendQueryString = true;
        const toDateArray = payload.toDate.split("/");
        const toDate =
          toDateArray[2] + "-" + toDateArray[0] + "-" + toDateArray[1];
        param += "&toDate=" + moment(new Date(toDate)).format("YYYY-MM-DD");
      }
      if (payload.dateFilterType === "custom") {
        const locale = "en-us";
        const label =
          new Date(payload.fromDate).toLocaleDateString(locale) +
          " to " +
          new Date(payload.toDate).toLocaleDateString(locale);
        setFilterLabel(label);
      }
      let filterChanged = false;
      if (payload.companyFilterChanged) {
        setCompanyFilterClass("filtered");
        setLocationFilterClass("filtered");
        setDateFilterClass("filtered");
        filterChanged = true;
      }
      if (payload.locationFilterChanged) {
        setLocationFilterClass("filtered");
        setDateFilterClass("filtered");
        filterChanged = true;
      }
      if (payload.dateFilterChanged) {
        setDateFilterClass("filtered");
        filterChanged = true;
      }
      if (filterChanged === false) {
        setCompanyFilterClass("");
        setLocationFilterClass("");
        setDateFilterClass("");
      }
      setLoading(true);
      const apiUrl = appendQueryString
        ? DashBoardApi.DASHBOARD_LIST + param
        : DashBoardApi.DASHBOARD_LIST;
      const { data } = await axios.get(apiUrl);
      if (data?.error === 0) {
        setLoading(false);
        const CarwashDashboardData = {
          carwash_company_count: data?.data?.carWashCount
            ? data?.data?.carWashCount
            : 0,
          wash_location_count: data?.data?.locationCount
            ? data?.data?.locationCount
            : 0,
          automatic_wash_location: data?.data?.automaticBayCount
            ? data?.data?.automaticBayCount
            : 0,
          tunnel_wash_location: data?.data?.tunnelBayCount
            ? data?.data?.tunnelBayCount
            : 0,
          selfBayCount: data?.data?.selfBayCount ? data?.data?.selfBayCount : 0,
          dogBayCount: data?.data?.dogBayCount ? data?.data?.dogBayCount : 0,
          vacuumBayCount: data?.data?.vacuumBayCount
            ? data?.data?.vacuumBayCount
            : 0,
          app_users: data?.data?.appUsers ? data?.data?.appUsers : 0,
          subscribed_users: data?.data?.subscribedUsers
            ? data?.data?.subscribedUsers
            : 0,
          active_hardware: data?.data?.activeHardware
            ? data?.data?.activeHardware
            : 0,
          inactive_hardware: data?.data?.inactiveHardware
            ? data?.data?.inactiveHardware
            : 0,
          current_month_washes: data?.data?.washesCompleted
            ? data?.data?.washesCompleted
            : 0,
          average_visit_permonth: data?.data?.averageWashesCompleted
            ? data?.data?.averageWashesCompleted
            : 0,
          current_month_revenue: data?.data?.revenue ? data?.data?.revenue : 0,
          last_device_sync_date: data?.data?.statusUpdateOn
            ? data?.data?.statusUpdateOn
            : "",
        };
        setDashboardCount(CarwashDashboardData);
      } else {
        setLoading(false);
        setDashboardCount(defaultCarwashDashboard);
      }
    } catch (error: any) {
      console.error("Error fetching company information:", error.message);
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const filterDashBoard = async (payload: any) => {
    getDashboardData(payload);
  };
  //Synch Dashboard Data
  const synchDeviceStatus = async () => {
    try {
      setLoading(true);
      //const apiUrl = DashBoardApi.SYNCH_DEVICE_STATUS;
      const apiUrl = DashBoardApi.SYNCH_DEVICE_STATUS_V2;
      const { data } = await axios.post(apiUrl);
      if (data?.error === 0) {
        getDashboardData(dashBoardFilterData);
      } else {
        setLoading(false);
      }
    } catch (error: any) {
      console.error("Error fetching device status:", error.message);
      setLoading(false);
      //throw new Error(error.response?.data?.message || error.message);
    }
  };

  return (
    <div className="dashboard-container">
      <Container>
        <div className="filter-top-wrap mb-3 text-end">
          <FilterDropdown
            userRole={userType["SUPER-ADMIN"]}
            filterDashBoard={filterDashBoard}
          />
        </div>
        <div className="db-information">
          <Row>
            <Col lg={5}>
              <div
                className={
                  "dashboard-card company-card position-relative " +
                  companyFilterClass
                }
              >
                <h4>Car Wash Companies</h4>
                <div className="info-sec d-flex justify-content-between">
                  <div className="count d-flex align-items-center">
                    <h1 className="f-bold">
                      {dashboardCount.carwash_company_count}
                    </h1>
                  </div>
                  <div className="db-icon">
                    <img src={Usersicon} alt="Car Wash App" />
                  </div>
                </div>
                <Button
                  title="Details"
                  variant="secondary"
                  className="d-flex align-items-center secondary-btn btn-details"
                  onClick={() =>
                    navigate(`/superadmin/${pageURLs.manage_carwash}`)
                  }
                >
                  Details <RightarrowIcon />
                </Button>
              </div>
            </Col>
            <Col lg={7}>
              <div
                className={
                  "dashboard-card location-card " + locationFilterClass
                }
              >
                <h4>WASH LOCATIONS</h4>
                <div className="info-sec d-flex justify-content-between align-items-center">
                  <div className="count">
                    <h1 className="f-bold">
                      {dashboardCount.wash_location_count}
                    </h1>
                    {/* <Button variant="secondary" className="d-flex align-items-center secondary-btn btn-details">
                                            Details <RightarrowIcon />
                                        </Button> */}
                  </div>
                  <div className="equipment-info">
                    <p className="f-semi mb-2">Wash Bay</p>
                    <div className="card-group d-flex">
                      <div className="card-sm automatic">
                        <p className="f-medium">In Bay</p>
                        <h6 className="f-bold">
                          {dashboardCount.automatic_wash_location}
                        </h6>
                      </div>
                      <div className="card-sm tunnel">
                        <p className="f-medium">Tunnel</p>
                        <h6 className="f-bold">
                          {dashboardCount.tunnel_wash_location}
                        </h6>
                      </div>
                      <div className="card-sm self-serve">
                        <p className="f-medium">Self Serve</p>
                        <h6 className="f-bold">
                          {dashboardCount.selfBayCount}
                        </h6>
                      </div>
                      <div className="card-sm dog-wash">
                        <p className="f-medium">Dog Wash</p>
                        <h6 className="f-bold">{dashboardCount.dogBayCount}</h6>
                      </div>
                      <div className="card-sm vaccum">
                        <p className="f-medium">Vaccum</p>
                        <h6 className="f-bold">
                          {dashboardCount.vacuumBayCount}
                        </h6>
                      </div>
                    </div>
                  </div>

                  {/* <div className="db-icon"><img src={Washlocationicon} alt="Car Wash App" /></div> */}
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg={5}>
              <div
                className={"dashboard-card users-card " + companyFilterClass}
              >
                <h4>APP USERS</h4>
                <div className="info-sec d-flex justify-content-between align-items-center">
                  <div className="count">
                    <h1 className="f-bold">{dashboardCount.app_users}</h1>
                  </div>
                  <div className="sub-card">
                    <p className="f-medium">Subscribed Users</p>
                    <h2 className="f-bold">
                      {dashboardCount.subscribed_users}
                    </h2>
                  </div>
                  <div className="db-icon">
                    <img src={Usersicon} alt="Car Wash App" />
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={7}>
              <div
                className={
                  "dashboard-card hardware-card position-relative " +
                  locationFilterClass
                }
              >
                <h4>HARDWARE</h4>
                <div className="info-sec d-flex justify-content-between align-items-center">
                  <div
                    className="activity-card active"
                    onClick={() => handleClick("active")}
                  >
                    <p className="f-medium">Active</p>
                    <div className="count d-flex align-items-center">
                      <h2 className="f-bold">
                        {dashboardCount.active_hardware}
                      </h2>
                    </div>
                    <Button
                      title="Details"
                      variant="secondary"
                      className="d-flex align-items-center secondary-btn btn-details"
                    >
                      Details <RightarrowIcon />
                    </Button>
                  </div>
                  <div
                    className="activity-card inactive"
                    onClick={() => handleClick("inactive")}
                  >
                    <p className="f-medium">Inactive</p>
                    <div className="count d-flex align-items-center">
                      <h2 className="f-bold">
                        {dashboardCount.inactive_hardware}
                      </h2>
                    </div>
                    <Button
                      title="Details"
                      variant="secondary"
                      className="d-flex align-items-center secondary-btn btn-details"
                    >
                      Details <RightarrowIcon />
                    </Button>
                  </div>
                  <div className="db-icon">
                    <img src={Hardwareicon} alt="Car Wash App" />
                  </div>
                </div>
                <div className="sync-sec">
                  {dashboardCount.last_device_sync_date && (
                    <p>
                      Last updated on{" "}
                      {formatDateTimeToUserTime(
                        dashboardCount.last_device_sync_date
                      )}
                    </p>
                  )}
                  <Button
                    title="Sync now"
                    variant="secondary"
                    className="d-flex align-items-center secondary-btn btn-details"
                    onClick={() => synchDeviceStatus()}
                  >
                    Sync now <SyncIcon />
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg={5}>
              <div
                className={"dashboard-card completed-card " + dateFilterClass}
              >
                <h4>washes completed</h4>
                <div className="info-sec d-flex justify-content-between align-items-center">
                  <div className="count d-flex flex-column">
                    <div className="month-label f-medium">{filterLabel}</div>
                    <h1 className="f-bold">
                      {dashboardCount.current_month_washes}
                    </h1>
                  </div>
                  <div className="sub-card">
                    <p>Average Visits /30 Days</p>
                    <h2 className="f-bold">
                      {dashboardCount.average_visit_permonth}
                    </h2>
                  </div>
                  <div className="db-icon">
                    <img src={Washcompleteicon} alt="Car Wash App" />
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={7}>
              <Row>
                <Col lg={7}>
                  <div
                    className={
                      "dashboard-card revenue-card position-relative " +
                      dateFilterClass
                    }
                  >
                    <h4>REVENUE</h4>
                    <div className="info-sec d-flex justify-content-between align-items-center">
                      <div className="revenue-info">
                        <div className="month-label f-medium">
                          {filterLabel}
                        </div>
                        <div className="count d-flex align-items-center">
                          <h1 className="f-bold">
                            $ {dashboardCount.current_month_revenue}
                          </h1>
                        </div>
                      </div>
                      <div className="db-icon">
                        <img src={Revenueicon} alt="Car Wash App" />
                      </div>
                    </div>
                    <Button
                      title="Details"
                      variant="secondary"
                      className="d-flex align-items-center secondary-btn btn-details"
                      onClick={() =>
                        navigate(`/superadmin/${pageURLs.revenue_reports}`)
                      }
                    >
                      Details <RightarrowIcon />
                    </Button>
                  </div>
                </Col>
                <Col lg={5}>
                  <div
                    className="customer-support-desk d-flex align-items-center justify-content-between"
                    onClick={() =>
                      navigate(`/superadmin/${pageURLs.customer_support_desk}`)
                    }
                  >
                    <h5>
                      Customer <br />
                      <span className="f-bold">Support</span> Desk
                    </h5>
                    <img src={Supporticon} alt="Car Wash App" />
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </Container>
      {loading && <SmallLoader />}
    </div>
  );
};

export default Dashboard;
